/**
 * contributionBasisReportModel.js — the Contribution Basis report's pure model
 * (PRD_ContributionBasisReport C3/C17/C19).
 *
 * PURE: no jsPDF, no Firestore, no clock — `todayYMD` is passed in. Reads only
 * what saved payroll run lines carry (C2): `deductions.<agency>` month maps,
 * `*BasisResult`, and the line's own `employment` snapshot. Never recomputes.
 */
import { toYMDString } from '../utils/periodDate';
import { employeeFullName } from '../utils/employeeSearch';
import { round2 } from './contributionTableClient';
import { SSS_BASIS_FACTORS } from '../constants/sssBasisFactors';
import { suppressionReason, boundNote } from '../constants/basisNotes';

// The basis-off row's label (C3) — printed as that row's note.
export const STORED_NOTE = 'Stored figure — not derived';

export const AGENCIES = {
  sss: { label: 'SSS', resultKey: 'sssBasisResult', storedKey: 'sssContribution' },
  philhealth: { label: 'PhilHealth', resultKey: 'philhealthBasisResult', storedKey: 'philhealthContribution' },
};

const shiftMonth = (monthKey, delta) => {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** 'YYYY-MM' → the following 'YYYY-MM' (December wraps). */
export const nextMonth = (monthKey) => shiftMonth(monthKey, 1);

const priorMonth = (monthKey) => shiftMonth(monthKey, -1);

// Index bounds may be strings or (legacy) Timestamps — toYMDString absorbs both.
const startMonthOf = (run) => toYMDString(run.periodStart).slice(0, 7);
const endMonthOf = (run) => toYMDString(run.periodEnd).slice(0, 7);

/**
 * C3's run rule: completed runs that can file a deduction under `monthKey` —
 * basis-off lines file under their period's own months, basis-on lines under
 * the month before their period starts, so the window is M and M+1.
 */
export const selectCandidateRuns = (indexRuns, monthKey) => {
  const next = nextMonth(monthKey);
  return (indexRuns || []).filter((r) => {
    if (!r || r.status !== 'completed') return false;
    const start = startMonthOf(r);
    const end = endMonthOf(r);
    return Boolean(start && end) && start <= next && end >= monthKey;
  });
};

/**
 * The month the report opens on: the month before the latest completed run's
 * start month (a basis-on month only has rows once a run starting the month
 * after is approved), capped at the previous Manila month; the previous Manila
 * month when no run is completed.
 */
export const defaultMonth = (indexRuns, todayYMD) => {
  const cap = priorMonth(todayYMD.slice(0, 7));
  const starts = (indexRuns || [])
    .filter((r) => r && r.status === 'completed')
    .map(startMonthOf)
    .filter(Boolean)
    .sort();
  if (starts.length === 0) return cap;
  const candidate = priorMonth(starts[starts.length - 1]);
  return candidate < cap ? candidate : cap;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * One row per employee for `month` and `agency` (C3). Lines are keyed by the
 * run line's employeeId (its doc id), so duplicate-ID employee documents —
 * already one line per run — stay one row.
 *
 * @param {Object} args
 * @param {Array} args.runs - candidate index runs (selectCandidateRuns)
 * @param {Object<string, Array>} args.linesByRunId - saved lines per runId
 * @param {'sss'|'philhealth'} args.agency
 * @param {string} args.month - 'YYYY-MM'
 * @returns {{ rows: Array, factorKeys: string[] }}
 */
export const buildContributionBasisRows = ({ runs, linesByRunId, agency, month }) => {
  const { label, resultKey, storedKey } = AGENCIES[agency];
  const ordered = [...(runs || [])].sort((a, b) =>
    toYMDString(a.periodStart).localeCompare(toYMDString(b.periodStart))
  );

  const byEmployee = new Map();
  for (const run of ordered) {
    const overlapsMonth = startMonthOf(run) <= month && endMonthOf(run) >= month;
    for (const line of linesByRunId[run.runId] || []) {
      const employeeId = String(line.employeeId ?? line.id);
      const acc = byEmployee.get(employeeId) || {
        employeeId, line: null, deducted: 0, composed: null, stored: null, include: false,
      };
      acc.line = line; // latest line — the name source

      // Only month-keyed maps: the shape Approve writes to the ledger.
      const byMonth = line.deductions?.[agency];
      const deducted = byMonth && typeof byMonth === 'object' ? num(byMonth[month]) : 0;
      if (deducted > 0) {
        acc.deducted += deducted;
        acc.include = true;
      }

      const result = line[resultKey];
      if (result?.applicableMonth === month) {
        acc.composed = result; // later runs overwrite — the latest wins
        acc.include = true;
      } else if (!result && overlapsMonth) {
        acc.stored = num(line.employment?.[storedKey]);
        if (acc.stored > 0) acc.include = true;
      }
      byEmployee.set(employeeId, acc);
    }
  }

  const rows = [];
  for (const acc of byEmployee.values()) {
    if (!acc.include) continue;
    const deducted = round2(acc.deducted);
    let row;
    if (acc.composed) {
      const r = acc.composed;
      const suppressed = r.amount === null || r.amount === undefined;
      row = {
        regime: suppressed ? 'suppressed' : 'composed',
        components: r.components || [],
        basis: r.basis ?? null,
        // no-table: the contribution is unknown, not zero.
        contribution: suppressed ? (r.suppressedBy === 'no-table' ? null : 0) : num(r.amount),
        note: suppressed ? suppressionReason(r.suppressedBy, month, label) : boundNote(r.atBound, label),
      };
    } else {
      row = { regime: 'stored', components: [], basis: null, contribution: acc.stored ?? 0, note: STORED_NOTE };
    }
    rows.push({
      employeeId: acc.employeeId,
      name: employeeFullName(acc.line),
      ...row,
      deducted,
      difference: row.contribution === null ? null : round2(row.contribution - deducted),
    });
  }
  rows.sort((a, b) => a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true }));

  // C19 (amended at build): a factor gets a column only when some row has a
  // non-zero amount for it — components carry every ticked factor, zeros
  // included, and a column of zeros costs width the landscape page lacks.
  const present = new Set(rows.flatMap((r) => r.components.filter((c) => c.amount !== 0).map((c) => c.factor)));
  const factorKeys = SSS_BASIS_FACTORS.map((f) => f.key).filter((k) => present.has(k));

  return { rows, factorKeys };
};
